import React from "react";
import useSWR from "swr";
import { Link } from "react-router-dom";
import { Users, MessageSquareWarning, Clock, AlertTriangle } from "lucide-react";
import { api, fmtDate, fmtDateTime, relativeTime } from "@/lib/api";
import { Skeleton, StatusPill, PriorityDot, StatCard } from "@/components/Atoms";

const fetcher = (u) => api.get(u).then((r) => r.data);

export default function CRMDashboard() {
  const { data: tickets } = useSWR("/tickets", fetcher);

  const open = (tickets || []).filter(t => ["open", "in_progress"].includes(t.status));
  const dueToday = open.filter(t => t.sla_deadline && new Date(t.sla_deadline) < new Date(Date.now() + 86400000));
  const breached = open.filter(t => t.sla_breached);
  const myCustomers = new Set((tickets || []).map(t => t.customer_id)).size;

  return (
    <div className="space-y-5">
      <div>
        <div className="rc-title-1">CRM Dashboard</div>
        <div className="rc-callout">Today&apos;s priorities, escalations and customer activity.</div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard testId="kpi-customers" label="My Customers" value={myCustomers}/>
        <StatCard testId="kpi-open" label="Open Tickets" value={open.length} accent="var(--rc-blue)"/>
        <StatCard testId="kpi-due" label="Due Today" value={dueToday.length} accent="var(--rc-warning)"/>
        <StatCard testId="kpi-breach" label="Breached" value={breached.length} accent="var(--rc-destructive)"/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 rc-card rc-card-padded">
          <div className="rc-title-3 mb-3">Today&apos;s action list</div>
          {!tickets && <Skeleton h={200}/>}
          {tickets && open.length === 0 && <div className="rc-footnote py-6 text-center">All caught up — great work.</div>}
          <div className="divide-y divide-[var(--rc-line)]">
            {open.sort((a,b) => (a.sla_breached === b.sla_breached ? 0 : a.sla_breached ? -1 : 1)).slice(0,8).map(t => (
              <Link to={`/crm/tickets/${t.ticket_id}`} key={t.ticket_id} className="py-3 flex items-center gap-3 hover:bg-[var(--rc-near-white)] -mx-2 px-2 rounded-lg" data-testid={`crm-todo-${t.ticket_id}`}>
                <PriorityDot priority={t.priority}/>
                <div className="flex-1 min-w-0">
                  <div className="rc-headline truncate">{t.subject}</div>
                  <div className="rc-footnote">{t.category} · SLA {fmtDateTime(t.sla_deadline)}</div>
                </div>
                <StatusPill status={t.sla_breached ? "breached" : t.status}/>
              </Link>
            ))}
          </div>
        </div>

        <div className="rc-card rc-card-padded">
          <div className="rc-title-3 mb-3">Recent activity</div>
          {!tickets && <Skeleton h={200}/>}
          <div className="divide-y divide-[var(--rc-line)]">
            {(tickets || []).slice(0, 6).map(t => (
              <div key={t.ticket_id} className="py-3 flex items-center gap-3">
                <MessageSquareWarning size={18} className="text-[var(--rc-blue)]"/>
                <div className="flex-1 min-w-0">
                  <div className="rc-body truncate">{t.subject}</div>
                  <div className="rc-caption">{relativeTime(t.updated_at)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
